/**
 * Client-side product filtering, sorting, and pagination utilities
 * Used for public ProductsPage to provide rich filtering without backend queries
 */

import { ProductSummary } from '../services/staticContent';

export interface PublicProductFilters {
  page: number;
  limit: number;
  search: string;
  category: string;
  tier: string;
  priceMin?: number;
  priceMax?: number;
  sortBy: 'title' | 'price' | 'publishedAt';
  sortOrder: 'asc' | 'desc';
}

export interface FilteredProductsResult {
  products: ProductSummary[];
  total: number;
  currentPage: number;
  totalPages: number;
}

/**
 * Filter products by search term (searches title + description)
 */
function searchProducts(
  products: ProductSummary[],
  searchTerm: string
): ProductSummary[] {
  if (!searchTerm.trim()) return products;

  const term = searchTerm.toLowerCase();
  return products.filter(
    (p) =>
      p.title.toLowerCase().includes(term) ||
      (p.shortDescription && p.shortDescription.toLowerCase().includes(term))
  );
}

/**
 * Filter products by category
 */
function filterByCategory(
  products: ProductSummary[],
  category: string
): ProductSummary[] {
  if (!category) return products;
  return products.filter((p) => p.category === category);
}

/**
 * Filter products by quality tier
 */
function filterByTier(
  products: ProductSummary[],
  tier: string
): ProductSummary[] {
  if (!tier) return products;
  return products.filter((p) => p.qualityTier === tier);
}

/**
 * Filter products by price range
 */
function filterByPriceRange(
  products: ProductSummary[],
  min?: number,
  max?: number
): ProductSummary[] {
  return products.filter((p) => {
    if (min !== undefined && p.price.amount < min) return false;
    if (max !== undefined && p.price.amount > max) return false;
    return true;
  });
}

/**
 * Sort products
 */
function sortProducts(
  products: ProductSummary[],
  sortBy: string,
  sortOrder: 'asc' | 'desc'
): ProductSummary[] {
  const sorted = [...products].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    switch (sortBy) {
      case 'title':
        aValue = a.title.toLowerCase();
        bValue = b.title.toLowerCase();
        break;
      case 'price':
        aValue = a.price.amount;
        bValue = b.price.amount;
        break;
      case 'publishedAt':
        aValue = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
        bValue = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  return sorted;
}

/**
 * Paginate products
 */
function paginateProducts(
  products: ProductSummary[],
  page: number,
  limit: number
): ProductSummary[] {
  const offset = (page - 1) * limit;
  return products.slice(offset, offset + limit);
}

/**
 * Main filtering function - applies all filters, sorting, and pagination
 */
export function filterAndPaginateProducts(
  allProducts: ProductSummary[],
  filters: PublicProductFilters
): FilteredProductsResult {
  let filtered = allProducts;

  // Apply filters
  filtered = searchProducts(filtered, filters.search);
  filtered = filterByCategory(filtered, filters.category);
  filtered = filterByTier(filtered, filters.tier);
  filtered = filterByPriceRange(filtered, filters.priceMin, filters.priceMax);

  // Sort
  filtered = sortProducts(filtered, filters.sortBy, filters.sortOrder);

  // Calculate pagination
  const total = filtered.length;
  const totalPages = Math.ceil(total / filters.limit) || 1;
  const currentPage = Math.min(Math.max(1, filters.page), totalPages);

  // Paginate
  const paginated = paginateProducts(filtered, currentPage, filters.limit);

  return {
    products: paginated,
    total,
    currentPage,
    totalPages,
  };
}
